// Angular import
import { Component, EventEmitter, OnInit, OnDestroy, Output, Input, ChangeDetectionStrategy } from '@angular/core';
import { Location, LocationStrategy } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';

// project import
import { NavigationItem } from '../navigation';
import { DataSharingService } from 'src/utils/broadcast-service';
import { SharedService } from 'src/app/activity.service';
import { ExceptionQueueService } from 'src/app/demo/exceptionQueue/exception-queue.service';
import { PacketBucketService } from 'src/app/demo/packetBuckets/packet-bucket.service';
import {
  ALL_REFERRALS_BUCKET,
  DELETED_BUCKET,
  PACKET_BUCKET_ITEMS,
  PENDING_REFERRALS_BUCKET,
  PRIORITY_REFERRALS_BUCKET
} from 'src/app/demo/packetBuckets/packet-bucket.models';
import { DocumentsReceivedService } from 'src/app/demo/documentsReceived/documents-received.service';
import {
  DOCUMENTS_RECEIVED_ITEMS,
  DOCUMENTS_RECEIVED_PATH,
  DocumentsReceivedSummary
} from 'src/app/demo/documentsReceived/documents-received.models';
import { SignalRService } from 'src/app/services/signalr.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';

interface ExcSummary {
  total: number;
  legalGuardian: number;
  authorization: number;
  facilityChange: number;
  missingDocs: number;
  nonReferrals: number;
  duplicate: number;
  nonReferral: number;
  multiOrderManualReview: number;
  posManualReview: number;
  orderNotSupportedByFacility: number;
}

@Component({
  selector: 'app-nav-content',
  templateUrl: './nav-content.component.html',
  styleUrls: ['./nav-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NavContentComponent implements OnInit, OnDestroy {
  @Output() NavCollapsedMob = new EventEmitter<any>();
  @Output() NavCollapse = new EventEmitter<void>();
  @Input() navCollapsed = false;

  currentApplicationVersion = environment.appVersion;
  windowWidth = window.innerWidth;
  selectedIndex = 2;
  sidebarUsername = '';
  sidebarInitials = '';

  docSubmenuOpen = false;
  excSubmenuOpen = false;
  queueSubmenuOpen = false;
  packetSubmenuOpen = false;
  pendingReferralsSubmenuOpen = false;

  excSummary: ExcSummary = {
    total: 0,
    legalGuardian: 0,
    authorization: 0,
    facilityChange: 0,
    missingDocs: 0,
    nonReferrals: 0,
    duplicate: 0,
    nonReferral: 0,
    multiOrderManualReview: 0,
    posManualReview: 0,
    orderNotSupportedByFacility: 0
  };

  menus = [
    // Hidden from nav menu for now — do not remove, route/component still exist.
    // {
    //   index: 1,
    //   title: 'Dashboard',
    //   link: '/dashboard',
    //   route: '/dashboard',
    //   icon: 'dashboard',
    //   queryParams: {}
    // },
    // Documents Received is now its own submenu (docSubmenus below), rendered ahead of these items.
    // The Files Received by-date page it used to open (/BotQueue/Index) still exists but is no
    // longer linked from the menu.
    // Hidden from nav menu for now — do not remove, route/component still exist.
    // {
    //   index: 3,
    //   title: 'Pages By Type',
    //   link: '/miscellanous-page',
    //   route: '/miscellanous-page',
    //   icon: 'tag',
    //   queryParams: {}
    // },
    {
      index: 6,
      title: 'Web Referral',
      link: '/web-referral',
      route: '/web-referral',
      icon: 'global',
      queryParams: {},
      // Admin-only for now — visibleMenus filters on this. Build Referral lives in the Admin submenu.
      roles: ['SystemAdmin', 'Administrator']
    },
    {
      index: 7,
      title: 'Dashboard Analytics',
      link: '/dashboard-analytics',
      route: '/dashboard-analytics',
      icon: 'area-chart',
      queryParams: {}
    },
  ];

  // Moved out of the flat `menus` list into their own "Admin" submenu (below) —
  // SystemAdmin/Administrator only, same role gate as before via showAdminMenu.
  adminSubmenuOpen = false;
  adminSubmenus: Array<{ title: string; link: string; icon: string }> = [
    { title: 'User Management', link: '/user-list', icon: 'team' },
     { title: 'Create Document', link: '/keyword-management', icon: 'file-add' },
    { title: 'Page Entity', link: '/page-entities-admin', icon: 'form' },
    { title: 'Page Layout', link: '/page-layout', icon: 'appstore' },
    { title: 'Tenant Config', link: '/tenant-config', icon: 'setting' },
    { title: 'Build Referral', link: '/build-referral', icon: 'build' }

  ];

  // Pill/count color now tracks each item's icon color (see .nav-badge in the stylesheet, which
  // reads the same --nav-icon-color custom property the icon does) — no per-item badge color here.
  exceptionSubmenus: Array<{ title: string; type: string | null; icon: string; countKey: string; link?: string }> = [
    { title: 'All exceptions', type: null, icon: 'alert', countKey: 'total' },
    { title: 'Legal guardian', type: 'Legal guardian', icon: 'safety', countKey: 'legalGuardian' },
    { title: 'Facility change', type: 'Facility change', icon: 'bank', countKey: 'facilityChange' },
    { title: 'Missing docs', type: 'Missing docs', icon: 'file-exclamation', countKey: 'missingDocs' },
    // Duplicate packets are listed under Packet Buckets > Duplicate Enrollment instead, and the
    // backend leaves them out of All exceptions.
    {
      title: 'Multi Order Manual Review',
      type: 'Multi Order Manual Review',
      icon: 'solution',
      countKey: 'multiOrderManualReview'
    },
    // rq_status 28 / 29.
    { title: 'POS Manual Review', type: 'POS Manual Review', icon: 'environment', countKey: 'posManualReview' },
    {
      title: 'Order Not Supported By Facility',
      type: 'Order Not Supported By Facility',
      icon: 'minus-circle',
      countKey: 'orderNotSupportedByFacility'
    },
    // Hidden from nav menu for now — need it for future, do not remove. Superseded by the
    // rq_status = 7 'Non Referral' entry below for the exception total count.
    // { title: 'Non Referrals', type: null, icon: 'file-unknown', countKey: 'nonReferrals', link: '/non-referrals' },
    { title: 'Non Referral', type: 'Non Referral', icon: 'file-unknown', countKey: 'nonReferral' }
  ];

  // Documents Received submenu — one item per Files Received column, each opening the document list
  // for that status. Each item's icon/badge color comes from its own `color` in DOCUMENTS_RECEIVED_ITEMS.
  readonly docSubmenus = DOCUMENTS_RECEIVED_ITEMS;
  readonly docReceivedPath = DOCUMENTS_RECEIVED_PATH;
  docSummary: Partial<DocumentsReceivedSummary> = {};

  queueSubmenus = [
    { title: 'All referrals', link: '/incoming-referral', icon: 'file-text', queryParams: {} },
    { title: 'My queue', link: '/incoming-referral', icon: 'profile', queryParams: { myQueue: 'true' } },
    { title: 'On hold', link: '/incoming-referral', icon: 'pause-circle', queryParams: { referralStatus: 'on_hold' } }
  ];

  readonly allReferralsBucket = ALL_REFERRALS_BUCKET;
  readonly deletedBucket = DELETED_BUCKET;
  readonly pendingReferralsBucket = PENDING_REFERRALS_BUCKET;
  packetSummary: Record<string, number> = {};
  // Shared with the header bell's Referral Queue panel (nav-right) so both offer the same buckets.
  packetSubmenus: Array<{ title: string; bucket: string; icon: string }> = PACKET_BUCKET_ITEMS.map(({ bucket, icon }) => ({
    title: bucket,
    bucket,
    icon
  }));

  // Pending Referrals is the one bucket the submenu nests further — "Pending" is the bucket as-is
  // (no priority filter), "Priority" scopes it to Crisis + ASAP only via ?priorityOnly=true (see
  // incoming-referral.component.ts buildQueueParams). Like Packet Buckets' own submenu-title, the
  // Pending Referrals row itself carries no badge — only these leaf items do (countKey into
  // packetSummary, same as every other bucket's getPacketCount(p.bucket)).
  readonly pendingReferralsSubmenus: Array<{ title: string; icon: string; priorityOnly: boolean; countKey: string }> = [
    { title: 'Pending', icon: 'unordered-list', priorityOnly: false, countKey: PENDING_REFERRALS_BUCKET },
    { title: 'Priority', icon: 'thunderbolt', priorityOnly: true, countKey: PRIORITY_REFERRALS_BUCKET }
  ];

  private routerSub: Subscription;
  private summarySub: any;
  private signalRSub: Subscription;

  // The refresh interval and SignalR pushes both call loadExcSummary(), so two can overlap and
  // land out of order, leaving the sidebar badges on the older counts.
  private excSummaryAbort: AbortController | null = null;
  private packetSummaryAbort: AbortController | null = null;
  private docSummaryAbort: AbortController | null = null;

  constructor(
    public nav: NavigationItem,
    private location: Location,
    private locationStrategy: LocationStrategy,
    private dataSharingService: DataSharingService,
    private router: Router,
    private sharedService: SharedService,
    private excService: ExceptionQueueService,
    private packetService: PacketBucketService,
    private docReceivedService: DocumentsReceivedService,
    private signalR: SignalRService,
    private authService: AuthService
  ) {}

  get visibleMenus() {
    return this.menus.filter((menu: any) => !menu.roles || this.authService.hasRole(menu.roles));
  }

  // Same role gate the flat items used before being moved into the Admin submenu.
  get showAdminMenu(): boolean {
    return this.authService.hasRole(['SystemAdmin', 'Administrator']);
  }

  ngOnInit() {
    const userData = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')) : {};
    this.sidebarUsername = ((userData?.FirstName || '') + ' ' + (userData?.LastName || '')).trim();
    this.sidebarInitials = ((userData?.FirstName?.charAt(0) || '') + (userData?.LastName?.charAt(0) || '')).toUpperCase();

    if (this.windowWidth < 1025) {
      (document.querySelector('.coded-navbar') as HTMLDivElement)?.classList.add('menupos-static');
    }

    // Track route changes to keep submenus open correctly
    this.updateSubmenuState();
    this.routerSub = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.updateSubmenuState());

    // Load documents received + exception + packet bucket counts immediately, then refresh every
    // 60 s (fallback). Documents change status outside this API (no SignalR push), so the documents
    // received badges rely on this interval alone.
    this.loadDocSummary();
    this.loadExcSummary();
    this.loadPacketSummary();
    this.summarySub = setInterval(() => {
      this.loadDocSummary();
      this.loadExcSummary();
      this.loadPacketSummary();
    }, 60_000);

    // Live updates via SignalR
    this.signalR.connect();
    this.signalRSub = this.signalR.exceptionSummaryChanged$.subscribe(() => this.loadExcSummary());
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
    this.signalRSub?.unsubscribe();
    if (this.summarySub) clearInterval(this.summarySub);
    this.excSummaryAbort?.abort();
    this.packetSummaryAbort?.abort();
    this.docSummaryAbort?.abort();
  }

  private updateSubmenuState() {
    // On a collapsed sidebar an open submenu isn't an inline expansion — ng-zorro renders it as a
    // flyout floating over the page. Auto-opening it from the route meant every navigation back to
    // one of these screens popped that flyout open uninvited. Leave the submenus shut while
    // collapsed; the user can still open the flyout by clicking the icon.
    if (this.navCollapsed) {
      this.docSubmenuOpen = false;
      this.excSubmenuOpen = false;
      this.queueSubmenuOpen = false;
      this.packetSubmenuOpen = false;
      this.adminSubmenuOpen = false;
      this.pendingReferralsSubmenuOpen = false;
      return;
    }

    this.docSubmenuOpen = this.router.url.startsWith(DOCUMENTS_RECEIVED_PATH);
    this.excSubmenuOpen = this.router.url.startsWith('/exception-queue') || this.router.url.startsWith('/non-referrals');

    // Packet Buckets submenu items now route to the same /incoming-referral page as the Queue
    // submenu, distinguished only by the ?bucket= query param — so which submenu is "open" has to
    // be decided from that param, not just the path.
    const onIncomingReferral = this.router.url.startsWith('/incoming-referral');
    const queryParams = this.router.parseUrl(this.router.url).queryParams;
    const hasBucketParam = !!queryParams['bucket'];
    this.queueSubmenuOpen = onIncomingReferral && !hasBucketParam;
    this.packetSubmenuOpen = onIncomingReferral && hasBucketParam;
    this.pendingReferralsSubmenuOpen = onIncomingReferral && queryParams['bucket'] === this.pendingReferralsBucket;
    this.adminSubmenuOpen = this.adminSubmenus.some((s) => this.router.url.startsWith(s.link));
  }

  private async loadExcSummary() {
    this.excSummaryAbort?.abort();
    const controller = new AbortController();
    this.excSummaryAbort = controller;

    try {
      const res: any = await this.excService.getSummary(controller.signal);
      if (controller.signal.aborted) return;
      const d = res?.data?.data;
      if (d) this.excSummary = d;
    } catch {
      /* silent — nav badge is non-critical */
    }
  }

  getCount(key: string): number {
    return (this.excSummary as any)[key] ?? 0;
  }

  private async loadDocSummary() {
    this.docSummaryAbort?.abort();
    const controller = new AbortController();
    this.docSummaryAbort = controller;

    try {
      const res: any = await this.docReceivedService.getSummary(controller.signal);
      if (controller.signal.aborted) return;
      const d = res?.data?.data;
      if (d) this.docSummary = d;
    } catch {
      /* silent — nav badge is non-critical */
    }
  }

  getDocCount(key: keyof DocumentsReceivedSummary): number {
    return this.docSummary[key] ?? 0;
  }

  private async loadPacketSummary() {
    this.packetSummaryAbort?.abort();
    const controller = new AbortController();
    this.packetSummaryAbort = controller;

    try {
      const counts = await this.packetService.getBucketCounts(controller.signal);
      if (controller.signal.aborted) return;
      this.packetSummary = counts;
    } catch {
      /* silent — nav badge is non-critical */
    }
  }

  getPacketCount(bucket: string): number {
    return this.packetSummary[bucket] ?? 0;
  }

  onClick = (menu: any) => {
    this.selectedIndex = menu.index;
    this.router.navigate([menu.link], { queryParams: menu.queryParams });
  };

  navMob() {
    if (this.windowWidth < 1025 && document.querySelector('app-navigation.coded-navbar')?.classList.contains('mob-open')) {
      this.NavCollapsedMob.emit();
    }
  }

  toggleNav() {
    this.NavCollapse.emit();
  }
}
